import type { ChatMessage, ToolMessage } from "../hooks/use-chat"

type ToolOutput<A> = ToolMessage & {
  output: A
}

export type ErrorOutput = ToolOutput<{
  error: string
}>

export function isErrorOutput(msg: ChatMessage): msg is ErrorOutput {
  return (
    msg.type === "tool" &&
    typeof (msg as ErrorOutput)?.output?.error === "string"
  )
}

export type QuestionOption = {
  id: string
  label: string
}

export type Question = {
  id: string
  text: string
  select: "single" | "multi"
  options: QuestionOption[]
  allow_free_text?: boolean
  skippable?: boolean
  link?: NavigateLink | null
}

export type QuestionsOutput = ToolOutput<{
  questions: Question[]
}>

export function isQuestionsOutput(msg: ChatMessage): msg is QuestionsOutput {
  return (
    msg.type === "tool" &&
    msg.name === "ask_questions" &&
    Array.isArray((msg as QuestionsOutput)?.output?.questions)
  )
}

export type QuestionAnswer = {
  question_id: string
  selected_option_ids: string[]
  free_text?: string
  skipped: boolean
}

export type ShowFileOutput = ToolOutput<{
  path: string
  query: Record<string, unknown> | null
  title?: string
}>

export function isShowFile(msg: ChatMessage): msg is ShowFileOutput {
  return (
    msg.type === "tool" &&
    msg.name === "show" &&
    typeof (msg as ShowFileOutput)?.output?.path === "string"
  )
}

export type NavigateLink = {
  path: string
  title: string
  query: Record<string, unknown> | null
}

export type NavigateOutput = ToolOutput<
  NavigateLink & {
    links?: NavigateLink[]
  }
>

export function isNavigate(msg: ChatMessage): msg is NavigateOutput {
  return (
    msg.type === "tool" &&
    msg.name === "navigate" &&
    typeof (msg as NavigateOutput)?.output?.path === "string"
  )
}

export function navigateLinks(
  output: NavigateOutput["output"],
): NavigateLink[] {
  const { path, title, query } = output
  return output.links?.length ? output.links : [{ path, title, query }]
}

export type CardChip = {
  label: string
  href?: string | null
}

export type CardTableColumn = {
  label?: string | null
  align?: "left" | "right"
}

export type CardHeadingBlock = {
  type: "heading"
  text: string
}

export type CardTextBlock = {
  type: "text"
  label?: string | null
  text: string
  style?: string | null
}

export type CardTableBlock = {
  type: "table"
  label?: string | null
  columns: CardTableColumn[]
  rows: string[][]
}

export type CardChipsBlock = {
  type: "chips"
  label?: string | null
  chips: CardChip[]
}

export type CardPhoto = {
  document_id: string
  file_name?: string | null
}

export type CardPhotosBlock = {
  type: "photos"
  label?: string | null
  photos: CardPhoto[]
}

export type CardButton = {
  label: string
  level: "primary" | "secondary"
}

export type CardButtonsBlock = {
  type: "buttons"
  label?: string | null
  buttons: CardButton[]
}

export type CardBlock =
  | CardHeadingBlock
  | CardTextBlock
  | CardTableBlock
  | CardChipsBlock
  | CardPhotosBlock
  | CardButtonsBlock

export type Card = {
  title: string
  badge: string | null
  blocks: CardBlock[]
}

export type CardOutput = ToolOutput<{
  cards: Card[]
}>

export function isCards(msg: ChatMessage): msg is CardOutput {
  return (
    msg.type === "tool" &&
    msg.name === "show_cards" &&
    Array.isArray((msg as CardOutput)?.output?.cards)
  )
}

export type SquawkOutput = ToolOutput<{
  squawk_ids: string[]
  squawk_numbers: Record<string, string>
  needs_photo?: string[]
}>

export function isSquawks(msg: ChatMessage): msg is SquawkOutput {
  return (
    msg.type === "tool" &&
    msg.name === "squawk" &&
    Array.isArray((msg as SquawkOutput)?.output?.squawk_ids)
  )
}
